import { useState } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";

import { TWO_FACTOR_MODAL_Z_INDEX } from "../TwoFactorVerificationModal/TwoFactorVerificationModal";

import { IntroductionStep } from "./IntroductionStep/IntroductionStep";
import { Mandatory2FAStep } from "./Mandatory2FAStep/Mandatory2FAStep";
import { Mandatory2FAStepName } from "./types";
import { useMandatory2FASetup } from "./useMandatory2FASetup";

import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardDialog } from "@/dashboard-design-system/Dialog";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { ActivateTotp } from "@/features/SettingsPage/Account/Modals/TwoFactorAuthSettingModal/steps/ActivateTotp/ActivateTotp";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { TotpRecoveryCodes } from "@/features/SettingsPage/Account/Modals/TwoFactorAuthSettingModal/steps/TotpRecoveryCodes/TotpRecoveryCodes";
import { ChevronLeft } from "@/icons/outlined";

const MODAL_Z_INDEX = TWO_FACTOR_MODAL_Z_INDEX - 1;

export const Mandatory2FASetupModal = () => {
  const {
    shouldDisplayModal,
    step,
    setStep,
    form,
    isLoadingSendSms,
    isSendButtonDisabled,
    sendSmsCooldown,
    onSubmitForm,
    submitOnKeyDown,
  } = useMandatory2FASetup();

  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  // Keep the modal open while showing recovery codes, even after refetchUser makes shouldDisplayModal false
  const [showingRecoveryCodes, setShowingRecoveryCodes] = useState(false);

  if (!shouldDisplayModal && !showingRecoveryCodes) {
    return null;
  }

  const renderStep = () => {
    switch (step) {
      case Mandatory2FAStepName.Introduction:
        return (
          <IntroductionStep
            onSetupSms={() => setStep(Mandatory2FAStepName.PhoneNumber)}
            onSetupTotp={() => setStep(Mandatory2FAStepName.ActivateTotp)}
          />
        );
      case Mandatory2FAStepName.PhoneNumber:
        return (
          <Mandatory2FAStep
            form={form}
            isLoadingSendSms={isLoadingSendSms}
            isSendButtonDisabled={isSendButtonDisabled}
            sendSmsCooldown={sendSmsCooldown}
            onSubmitForm={onSubmitForm}
            submitOnKeyDown={submitOnKeyDown}
          />
        );
      case Mandatory2FAStepName.ActivateTotp:
        return (
          <ActivateTotp
            isOpen={true}
            onClose={() => setStep(Mandatory2FAStepName.Introduction)}
            onSuccess={(codes) => {
              setRecoveryCodes(codes);
              setShowingRecoveryCodes(true);
              setStep(Mandatory2FAStepName.TotpRecoveryCodes);
            }}
          />
        );
      case Mandatory2FAStepName.TotpRecoveryCodes:
        return (
          <TotpRecoveryCodes
            isOpen={true}
            onClose={() => setShowingRecoveryCodes(false)}
            recoveryCodes={recoveryCodes}
          />
        );
    }
  };

  if (step === Mandatory2FAStepName.ActivateTotp || step === Mandatory2FAStepName.TotpRecoveryCodes) {
    return renderStep();
  }

  return (
    <DashboardDialog
      open={true}
      fullScreen
      maxWidth={false}
      hideCloseButton
      vibe="brand"
      hideIcon
      sx={{ zIndex: MODAL_Z_INDEX }}
    >
      <Stack
        sx={{
          width: "100%",
          height: "calc(100dvh - 140px)",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {step !== Mandatory2FAStepName.Introduction && (
          <Box sx={{ width: "100%", maxWidth: 484, paddingBottom: 2 }}>
            <DashboardIconButton vibe="neutral" onClick={() => setStep(Mandatory2FAStepName.Introduction)}>
              <ChevronLeft />
            </DashboardIconButton>
          </Box>
        )}
        {renderStep()}
      </Stack>
    </DashboardDialog>
  );
};
